/* eslint-disable max-lines, complexity */
import { SelectableGroup } from '@cloud/react-selectable';
import app from 'Cloud/Application/app';
import { isNil, path } from 'ramda';
import React, { memo, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isNewWebSearchUIEnabled } from 'reactApp/appHelpers/featuresHelpers';
import { QuotaCleanerPlug } from 'reactApp/components/QuotaCleanerPlug/QuotaCleanerPlug';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { useHotKeyScope } from 'reactApp/hooks/useHotkeyScope';
import { usePortal } from 'reactApp/hooks/usePortal';
import { isDialogVisible } from 'reactApp/modules/dialog/dialog.selectors';
import { isMediaFolderAndGalleryEnabled, isVirusItem } from 'reactApp/modules/file/utils';
import { getGalleryCategory } from 'reactApp/modules/gallery/gallery.selectors';
import { isReadOnly, isThisOrParentMounted } from 'reactApp/modules/home/home.selectors';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { isPublicDownloadableEnabled, isPublicUploadEnabled } from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage, getParams } from 'reactApp/modules/router/router.selectors';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import {
    getSelectedItem,
    getStorageCurrentFolder,
    getStorageItemById,
    groupedIds,
    hasLoadMore,
} from 'reactApp/modules/storage/storage.selectors';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { selectFilesInProgress } from 'reactApp/modules/uploadList/uploadList.selectors';
import { getParams as getAttachesParams } from 'reactApp/sections/AttachesPage/helpers';
import { RootState } from 'reactApp/store';
import { WrapDataListItem } from 'reactApp/ui/Datalist/Item/DataListItem';
import { useHandleCallback } from 'reactApp/ui/Datalist/UserActions/handleCallback';
import { useHandleDragAndDropCallback } from 'reactApp/ui/Datalist/UserActions/handleDragAndDropCallback';
import { useHandleMouseCallback } from 'reactApp/ui/Datalist/UserActions/handleMouseCallback';
import { hotkeysHandlers } from 'reactApp/ui/Datalist/UserActions/hotkeys';
import { onScroll } from 'reactApp/ui/Datalist/UserActions/onScrollAction';
import { DataListDragItem } from 'reactApp/ui/DataListDragItem/DataListDragItem';
import { getThumbUrl } from 'reactApp/ui/DataListItem/DataListItem.helpers';
import { ID_NEW_ITEM } from 'reactApp/ui/DataListItemCreateNew/DataListItemCreateNew';
import { Empty } from 'reactApp/ui/Empty/Empty';
import { EmptyFolder } from 'reactApp/ui/EmptyFolder/EmptyFolder';
import { Loader } from 'reactApp/ui/Loader/Loader';
import { EmptySearch } from 'reactApp/ui/SearchMessage/EmptySearch/EmptySearch';
import { ErrorSearch } from 'reactApp/ui/SearchMessage/ErrorSearch/ErrorSearch';
import { VirtualList } from 'reactApp/ui/VirtualList/VirtualList';
import { IVirtualList } from 'reactApp/ui/VirtualList/VirtualList.types';

import { ErrorRetry } from '../ErrorRetry/ErrorRetry';
import { HOT_KEY_SCOPE_NAME, ROW_HEIGHT } from './DataList.constants';
import styles from './DataList.css';
import { getShowSecondClickTip } from './DataList.helpers';
import {
    useAutoSwitchFromGallery,
    useGaSelectSend,
    useGaShowGallery,
    useHandleMouse,
    useHandlerOnDocumentClick,
    useHandleStopDrag,
    useLoadToItem,
    useLogHandler,
    useScrollToItem,
    useSendDwh,
    useSendGaStat,
    useSetActiveIndex,
    useSetInitialViewerId,
} from './DataList.hooks';
import { Props } from './DataList.types';
import { DataListItemPlug } from './DataListItemPlug/DataListItemPlug';
import { ExtraBlocks } from './ExtraBlocks/ExtraBlocks';

export const DataList = memo(
    // eslint-disable-next-line max-lines-per-function
    ({
        goTo,
        storage,
        viewMode: currentViewMode,
        disableCenteredLoader = false,
        limitItemsNumber = 0,
        disableTips = false,
        gaSuffix = '',
        initialItemId = '',
        disableLoadOnScroll = false,
        isPopup = false,
        itemsList,
        customScrollElement,
        onItemSelectCallback,
    }: Props): ReactElement => {
        const startTime = Date.now();
        const dragParams = useRef<{
            dragItem: null | CloudItem;
            startX: number;
            startY: number;
            isDragging: boolean;
        }>({
            dragItem: null,
            startX: 0,
            startY: 0,
            isDragging: false,
        });
        const dispatch = useDispatch();
        const isDragging = useSelector(SelectionsSelectors.isDragging);
        const [dragPosX, setDragPosX] = useState(0);
        const [dragPosY, setDragPosY] = useState(0);
        const [dragOverItem, setDragOverItem] = useState<CloudItem | null>(null);
        const [selectingIdxs, setSelectingIdxs] = useState<string[]>([]);
        const [initialViewerId, setInitialViewerId] = useState<string>(initialItemId);
        const prevSelectHandler = useRef(null);
        const registerItem = useRef<null | any>(null);
        const unregisterItem = useRef<null | any>(null);
        const listRef = useRef<IVirtualList | null>(null);
        const itemCreateNewRef = useRef<HTMLDivElement | null>(null);
        const routerParams = useSelector(getParams);
        const currentStorage = useSelector(getCurrentStorage) as EStorageType;
        storage = storage || currentStorage;

        const folder = useSelector((state: RootState) => getStorageCurrentFolder(state, storage as EStorageType));

        const isReadOnlyFolder = useSelector((state: RootState) => isReadOnly(state, folder?.id));
        const { isHome, isPublic, isSharedLinks, isSearch, isSharedAutodelete, isAlbums, isQuotaCleaner } = getStorage(storage);
        const attachesParams = getAttachesParams({ params: useSelector(SettingsSelectors.getQueryParams) });
        let items = useSelector((state: RootState) => groupedIds(state, storage as EStorageType)) as string[];
        if (itemsList?.length) {
            items = itemsList;
        }

        const hasAddedItem = Boolean(
            (isHome && (folder?.childs?.length || folder?.id === ROOT_FOLDER_ID)) || isSharedLinks || isSharedAutodelete
        );
        const storageViewMode = useSelector((state) =>
            SettingsSelectors.getViewByStorage(state, storage, isMediaFolderAndGalleryEnabled(state, folder))
        );
        const viewMode = currentViewMode || storageViewMode;

        if (limitItemsNumber > 0) {
            items = items.slice(0, limitItemsNumber);
        }

        if (hasAddedItem && viewMode !== EViewMode.gallery) {
            items = [ID_NEW_ITEM, ...items];
        }

        const isMounted = useSelector((state: RootState) => isThisOrParentMounted(state, folder?.id));

        const selectedIdxs = useSelector(SelectionsSelectors.getSelectedIdxs);
        const lastSelectedId = useSelector(SelectionsSelectors.getLastSelected);
        const selectedRange = useSelector(SelectionsSelectors.getSelectedRange);
        const selectedCount = selectedIdxs.length;
        const selectedItems = useSelector((state: RootState) =>
            selectedIdxs.map((id) => getStorageItemById(state, storage as EStorageType, id)).filter((item) => !!item)
        );
        const filesInProgress = useSelector(selectFilesInProgress);

        const folderId = folder?.id || '';
        const isLoading = folder?.isLoading;
        const isLoaded = folder?.isLoaded;
        const error = path(['error'], folder);
        const hasMoreToLoad = useSelector((state: RootState) => hasLoadMore(state, storage, folder));

        const isFavoritesEnabled = (isHome || isSearch || isAlbums) && !isMounted;
        const draggingPortalTarget = usePortal('draggingElement');
        const isSearchActive = attachesParams?.query || routerParams?.searchType || routerParams?.query;
        const isModalOpen: boolean = useSelector(isDialogVisible);
        const isPublicUpload = useSelector(isPublicUploadEnabled);
        const isPublicDownloaded = useSelector(isPublicDownloadableEnabled);
        const galleryCategory = useSelector(getGalleryCategory);

        const secondClickPromo = useSelector(PromoSelectors.pullPromo(EPromoType.secondClick));

        const tipData = getShowSecondClickTip({ selectedCount, isDragging, disableTips, promo: secondClickPromo });

        useEffect(() => {
            if (secondClickPromo && tipData) {
                secondClickPromo.onShow();
            }
            if (secondClickPromo && !tipData) {
                secondClickPromo.onHide();
            }
        }, [secondClickPromo]);

        const { handleStartDrag, handleStopDrag, handleDraggingOver } = useHandleDragAndDropCallback({
            dragParams,
            selectedIdxs,
            dispatch,
            storage,
            setDragOverItem,
            setDragPosX,
            setDragPosY,
            prevSelectHandler,
            dragOverItem,
            selectedItems,
            isDragging,
        });

        const { handleMouseMove, handleMouseUp, handleMouseDownOnItem, handleMouseLeave } = useHandleMouseCallback({
            dragParams,
            storage,
            setDragOverItem,
            setDragPosX,
            setDragPosY,
            isDragging,
            handleStartDrag,
        });

        const loadOnScroll = useCallback(() => {
            onScroll({
                storage,
                items,
                hasAddedItem,
                folderId,
                hasMoreToLoad,
                dispatch,
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [disableLoadOnScroll, folderId, items.length, dispatch, hasAddedItem, hasMoreToLoad]);

        const [activeIndex, isElementLoaded, setActiveIndex] = useLoadToItem(items, loadOnScroll, isLoading);

        const selectedItem = useSelector(getSelectedItem);

        const { query, type: attachType, folderId: attachFolderId } = useSelector(SettingsSelectors.getQueryParams);

        useScrollToItem({ activeIndex, dispatch, listRef, isElementLoaded: isElementLoaded && !isLoading && isLoaded });

        useSetActiveIndex({ selectedItem, items, setActiveIndex, storage });

        useSetInitialViewerId({ isLoaded, loadOnScroll, initialViewerId, setInitialViewerId, items, storage, dispatch });

        useHandleStopDrag({ isDragging, handleStopDrag, selectedItems });

        useHandleMouse({ handleMouseMove, handleMouseUp });

        useAutoSwitchFromGallery({ viewMode, folder, storage });

        const {
            handleOnDocumentClick,
            handleOnSelectGroupClick,
            handleBeginSelection,
            handleOnContextMenu,
            handleOnContextMenuItem,
            handleOnSelect,
            handleSelectboxSelection,
            handleSelecting,
            handleOnClick,
            handleClickCreateNew,
        } = useHandleCallback({
            isModalOpen,
            setActiveIndex,
            dispatch,
            selectedItem,
            prevSelectHandler,
            storage,
            gaSuffix,
            items,
            selectedIdxs,
            selectingIdxs,
            setSelectingIdxs,
            goTo,
            routerParams,
            itemCreateNewRef,
            viewMode,
            isPopup,
            onItemSelectCallback,
            attachType,
            attachFolderId,
        });

        useHandlerOnDocumentClick({ handleOnDocumentClick });

        useLogHandler({ items, viewMode, startTime, folder });

        useGaSelectSend({ selectedCount });

        useGaShowGallery({ viewMode });

        useSendGaStat({ folder });

        useHotKeyScope(HOT_KEY_SCOPE_NAME);

        const { onItemsRendered } = useSendDwh({
            storage,
            isSearchActive,
            isLoaded,
            isError: Boolean(error),
            query,
            items,
        });

        const isNewSearchScreen = isNewWebSearchUIEnabled && isSearchActive;

        useEffect(
            (): (() => void) | undefined =>
                hotkeysHandlers({
                    folder,
                    selectedIdxs,
                    storage,
                    dispatch,
                    items,
                    handleOnClick,
                    viewMode,
                    isPublicUpload,
                    selectedItems,
                    hasAddedItem,
                    lastSelectedId,
                    listRef,
                    selectedRange,
                    filesInProgress,
                }),
            [
                items,
                selectedItems,
                storage,
                isPublicUpload,
                lastSelectedId,
                selectedRange,
                selectedIdxs,
                hasAddedItem,
                filesInProgress,
                folder,
                dispatch,
                handleOnClick,
                viewMode,
            ]
        );

        const renderItem = (id: string, options: { index: number; thumbType?: string }): ReactElement => (
            <WrapDataListItem
                id={id}
                optionIndex={options.index}
                view={viewMode}
                handleClickCreateNew={handleClickCreateNew}
                isSharedLinks={isSharedLinks}
                selectedIdxs={selectedIdxs}
                selectingIdxs={selectingIdxs}
                activeIndex={activeIndex}
                isDragging={isDragging}
                dragOverItemId={dragOverItem?.id}
                onSelect={handleOnSelect}
                onClick={handleOnClick}
                onContextMenu={handleOnContextMenuItem}
                selectedCount={selectedCount}
                storage={storage || EStorageType.home}
                tipData={tipData}
                isFavoritesEnabled={isFavoritesEnabled}
                onDraggingOver={handleDraggingOver}
                onMouseLeave={handleMouseLeave}
                register={registerItem.current}
                unregister={unregisterItem.current}
                onMouseDown={handleMouseDownOnItem}
                rootRef={itemCreateNewRef}
                thumbType={options.thumbType}
                isPopup={isPopup}
                isPublicDownloaded={isPublicDownloaded}
            />
        );

        if (error) {
            if (isNewSearchScreen && isSearch) {
                return <ErrorSearch data-qa-datalist={storage} />;
            }

            return (
                <div className={styles.root} data-qa-datalist={storage}>
                    <ErrorRetry centered={!isPublic && !disableCenteredLoader} />
                </div>
            );
        }

        if (((isNil(isLoading) || isLoading) && !isLoaded && !items.length) || !isElementLoaded) {
            if (isQuotaCleaner) {
                return <DataListItemPlug />;
            }

            return (
                <div className={styles.root} data-qa-datalist={storage}>
                    <Loader centered={!isPublic && !disableCenteredLoader} />
                </div>
            );
        }

        if (!items?.length) {
            // Не выводим для корня и разделов, только для папки
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (!isPublic && !isQuotaCleaner && folder?.id && folder.parent && !isReadOnlyFolder) {
                return (
                    <div onContextMenu={handleOnContextMenu} className={styles.root} data-qa-datalist={storage}>
                        <EmptyFolder folder={folder} />
                    </div>
                );
            }

            if (isQuotaCleaner) {
                return <QuotaCleanerPlug />;
            }

            if (isNewSearchScreen) {
                return (
                    <EmptySearch
                        data-qa-datalist={storage}
                        onContextMenu={handleOnContextMenu}
                        searchType={routerParams?.searchType}
                        searchText={routerParams?.query || attachesParams?.query}
                    />
                );
            }

            return (
                <div onContextMenu={handleOnContextMenu} className={styles.root} data-qa-datalist={storage}>
                    <Empty
                        searchType={isSearchActive && isSearch ? routerParams?.searchType : galleryCategory || null}
                        searchText={isSearchActive ? routerParams?.query || attachesParams?.query : undefined}
                        type={isSearchActive ? 'search' : storage === EStorageType.gallery ? 'gallery' : 'folder'}
                        storage={storage}
                        canUpload={isHome && !isReadOnlyFolder}
                        setInputs={app.upload?.setInputs}
                        weblink={app.publicUrl && app.publicUrl(folder)}
                        id={folder?.id}
                        canCoshare
                    />
                </div>
            );
        }

        return (
            <>
                <SelectableGroup
                    tolerance={20}
                    onSelection={handleSelecting}
                    onBeginSelection={handleBeginSelection}
                    preventDefault={false}
                    onClick={handleOnSelectGroupClick}
                    onEndSelection={handleSelectboxSelection}
                    selectboxClassName={styles.selectbox}
                    disableClickOnItem
                >
                    {({ register, unregister }) => {
                        registerItem.current = register;
                        unregisterItem.current = unregister;

                        return (
                            <div
                                className={styles.root}
                                onContextMenu={handleOnContextMenu}
                                onMouseLeave={handleMouseLeave}
                                data-qa-datalist={storage}
                                data-qa-view={viewMode}
                            >
                                <VirtualList
                                    hasMoreToLoad={disableLoadOnScroll ? false : hasMoreToLoad}
                                    list={items}
                                    viewMode={viewMode}
                                    renderItem={renderItem}
                                    rowHeight={ROW_HEIGHT}
                                    isLoading={isLoading}
                                    loadOnScroll={loadOnScroll}
                                    onItemsRendered={onItemsRendered}
                                    ref={listRef}
                                    customScrollElement={customScrollElement}
                                />
                                {isDragging &&
                                    selectedCount > 0 &&
                                    selectedItems[0] &&
                                    !!dragPosX &&
                                    !!dragPosY &&
                                    createPortal(
                                        <DataListDragItem
                                            dragPosX={dragPosX}
                                            dragPosY={dragPosY}
                                            selectedCount={selectedCount}
                                            item={selectedItems[0]}
                                            thumb={getThumbUrl(selectedItems[0], isVirusItem(selectedItems[0]))}
                                        />,
                                        draggingPortalTarget
                                    )}
                            </div>
                        );
                    }}
                </SelectableGroup>
                {!isQuotaCleaner && !isPopup && <ExtraBlocks itemsCount={items.length} />}
            </>
        );
    }
);

DataList.displayName = 'DataList';

DataList.whyDidYouRender = true;
